import { useCallback, useEffect, useRef, useState } from 'react';

// Cloudflare Turnstile loaded from CDN (no npm dependency) — same mechanism as
// ContactSection, extracted into a hook so new forms do not duplicate it.
declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
  }
}

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

export const useTurnstile = () => {
  const [token, setToken] = useState<string | null>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

  useEffect(() => {
    if (!siteKey) {
      console.warn('VITE_TURNSTILE_SITE_KEY is not set — Turnstile will not render.');
      return;
    }

    const renderWidget = () => {
      if (window.turnstile && widgetRef.current && widgetIdRef.current === null) {
        widgetIdRef.current = window.turnstile.render(widgetRef.current, {
          sitekey: siteKey,
          callback: (tok: string) => setToken(tok),
          'expired-callback': () => setToken(null),
          'error-callback': () => setToken(null),
          theme: 'auto',
        });
      }
    };

    let iv: number | undefined;
    let timeout: number | undefined;
    let script: HTMLScriptElement | null = null;

    if (window.turnstile) {
      renderWidget();
    } else {
      script = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SRC}"]`);
      if (!script) {
        script = document.createElement('script');
        script.src = TURNSTILE_SRC;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      script.addEventListener('load', renderWidget);
      iv = window.setInterval(() => {
        if (window.turnstile) {
          window.clearInterval(iv);
          renderWidget();
        }
      }, 200);
      timeout = window.setTimeout(() => window.clearInterval(iv), 8000);
    }

    return () => {
      if (iv) window.clearInterval(iv);
      if (timeout) window.clearTimeout(timeout);
      if (script) script.removeEventListener('load', renderWidget);
      if (window.turnstile && widgetIdRef.current) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* noop */ }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  const reset = useCallback(() => {
    setToken(null);
    if (window.turnstile && widgetIdRef.current) {
      try { window.turnstile.reset(widgetIdRef.current); } catch { /* noop */ }
    }
  }, []);

  return { token, widgetRef, reset, enabled: Boolean(siteKey) };
};
